import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { DELETE_STARTER_PACK_CONFIG, GET_STARTER_PACK_CONFIGS, UPSERT_STARTER_PACK_CONFIG } from './graphql';

type TriggerType = 'FEATURE_UNLOCK' | 'FIXED_LEVEL';

type PackReward = {
  type: string;
  amount: number;
  itemId?: string | null;
  boostType?: string | null;
  categoryId?: string | null;
};

type StarterPackConfigEntry = {
  id: string;
  enabled: boolean;
  feature: string;
  triggerType: TriggerType;
  triggerLevel?: number | null;
  rewards: PackReward[];
  value: number;
  cost: number;
  skuIos: string;
  skuAndroid: string;
  expiryHours: number;
  source: 'FIXTURE' | 'DB';
  effectiveLevel?: number | null;
};

const REWARD_TYPES = [
  'CURRENCY_COIN',
  'CURRENCY_DIAMOND',
  'CURRENCY_CATEGORY_FRAGMENT',
  'CURRENCY_BOOST',
  'CURRENCY_TOURNAMENT_TICKET',
  'CURRENCY_STAMP_CARD',
  'CURRENCY_META_FRAGMENT_BRONZE',
  'CURRENCY_META_FRAGMENT_SILVER',
  'CURRENCY_META_FRAGMENT_GOLD',
  'STICKER',
  'PROFILE_FRAME',
  'APP_SKIN',
];

const BOOST_TYPES = ['REMOVE2', 'TEAM_SPIRIT', 'REVIVE', 'BOMB', 'NINJA', 'SPEEDSTER', 'ARTIST_ONLY', 'HURDLE_HINT'];

const describeReward = (reward: PackReward): string => {
  const extras = [reward.boostType, reward.itemId, reward.categoryId].filter(Boolean).join(' ');
  return `${reward.amount}x ${reward.type}${extras ? ` (${extras})` : ''}`;
};

const StarterPacks = () => {
  const { data, loading, refetch } = useQuery(GET_STARTER_PACK_CONFIGS, { fetchPolicy: 'network-only' });
  const [upsertConfig, { loading: saving }] = useMutation(UPSERT_STARTER_PACK_CONFIG);
  const [deleteConfig, { loading: deleting }] = useMutation(DELETE_STARTER_PACK_CONFIG);

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusVariant, setStatusVariant] = useState<'success' | 'danger'>('success');
  const [editorError, setEditorError] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);

  const [editId, setEditId] = useState('');
  const [editEffectiveLevel, setEditEffectiveLevel] = useState<number | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [feature, setFeature] = useState('');
  const [triggerType, setTriggerType] = useState<TriggerType>('FEATURE_UNLOCK');
  const [triggerLevel, setTriggerLevel] = useState('');
  const [rewards, setRewards] = useState<PackReward[]>([]);
  const [value, setValue] = useState('3');
  const [cost, setCost] = useState('1.99');
  const [skuIos, setSkuIos] = useState('');
  const [skuAndroid, setSkuAndroid] = useState('');
  const [expiryHours, setExpiryHours] = useState('48');

  const packs: StarterPackConfigEntry[] = useMemo(
    () => data?.liveOps?.starterPackConfigs ?? [],
    [data?.liveOps?.starterPackConfigs]
  );

  const onEdit = (pack: StarterPackConfigEntry) => {
    setEditorError(null);
    setEditId(pack.id);
    setEditEffectiveLevel(pack.effectiveLevel ?? null);
    setEnabled(pack.enabled);
    setFeature(pack.feature);
    setTriggerType(pack.triggerType);
    setTriggerLevel(pack.triggerLevel != null ? String(pack.triggerLevel) : '');
    setRewards(pack.rewards.map((reward) => ({ ...reward })));
    setValue(String(pack.value));
    setCost(String(pack.cost));
    setSkuIos(pack.skuIos);
    setSkuAndroid(pack.skuAndroid);
    setExpiryHours(String(pack.expiryHours));
    setShowEditor(true);
  };

  const onSave = async () => {
    setEditorError(null);
    try {
      const parsedTriggerLevel = triggerLevel.trim() ? Number(triggerLevel) : null;
      if (triggerType === 'FIXED_LEVEL' && (!parsedTriggerLevel || parsedTriggerLevel < 1)) {
        throw new Error('Fixed-level trigger needs a level of 1 or higher.');
      }
      if (!rewards.length) {
        throw new Error('At least one reward is required.');
      }
      await upsertConfig({
        variables: {
          input: {
            id: editId,
            enabled,
            feature: feature.trim(),
            triggerType,
            triggerLevel: triggerType === 'FIXED_LEVEL' ? parsedTriggerLevel : null,
            rewards: rewards.map((reward) => ({
              type: reward.type,
              amount: Number(reward.amount),
              itemId: reward.itemId || null,
              boostType: reward.boostType || null,
              categoryId: reward.categoryId || null,
            })),
            value: Number(value),
            cost: Number(cost),
            skuIos: skuIos.trim(),
            skuAndroid: skuAndroid.trim(),
            expiryHours: Number(expiryHours),
          },
        },
      });
      setStatusVariant('success');
      setStatusMessage(`Pack "${editId}" saved.`);
      setShowEditor(false);
      await refetch();
    } catch (error) {
      setEditorError(error instanceof Error ? error.message : 'Failed to save pack.');
    }
  };

  const onResetToDefault = async (pack: StarterPackConfigEntry) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the admin override for "${pack.id}" and revert to the code default?`)) {
      return;
    }
    try {
      await deleteConfig({ variables: { id: pack.id } });
      setStatusVariant('success');
      setStatusMessage(`Pack "${pack.id}" reverted to code default.`);
      await refetch();
    } catch (error) {
      setStatusVariant('danger');
      setStatusMessage(error instanceof Error ? error.message : 'Failed to revert pack.');
    }
  };

  const setRewardField = (index: number, field: keyof PackReward, fieldValue: string) => {
    setRewards((current) =>
      current.map((reward, rewardIndex) =>
        rewardIndex === index
          ? { ...reward, [field]: field === 'amount' ? Number(fieldValue) : fieldValue }
          : reward
      )
    );
  };

  return (
    <>
      {statusMessage && (
        <Alert variant={statusVariant} onClose={() => setStatusMessage(null)} dismissible>
          {statusMessage}
        </Alert>
      )}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            Starter Packs — trigger, rewards and offer window per pack. FEATURE_UNLOCK follows the level configs;
            FIXED_LEVEL unlocks at an explicit level for everyone.
          </span>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning" className="small">
            SKUs and USD cost must match products registered in App Store Connect / Play Console — the admin cannot
            create new price points. Changes reach game servers within ~1 minute.
          </Alert>
          {loading ? (
            <Spinner animation="border" />
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Pack</th>
                  <th>Level</th>
                  <th>Status</th>
                  <th>Trigger</th>
                  <th>Rewards</th>
                  <th>Price / SKUs</th>
                  <th>Offer window</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {packs.map((pack) => (
                  <tr key={pack.id}>
                    <td>
                      {pack.id}
                      <Badge variant={pack.source === 'DB' ? 'info' : 'secondary'} className="ml-2">
                        {pack.source === 'DB' ? 'Configured' : 'Code default'}
                      </Badge>
                    </td>
                    <td>{pack.effectiveLevel ?? '—'}</td>
                    <td>
                      <Badge variant={pack.enabled ? 'success' : 'secondary'}>
                        {pack.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td>
                      {pack.triggerType === 'FIXED_LEVEL'
                        ? `Level ${pack.triggerLevel}`
                        : `Feature: ${pack.feature}`}
                    </td>
                    <td>{pack.rewards.map(describeReward).join(', ')}</td>
                    <td>
                      ${pack.cost}
                      <div className="text-muted small">{pack.skuIos}</div>
                      <div className="text-muted small">{pack.skuAndroid}</div>
                    </td>
                    <td>{pack.expiryHours}h</td>
                    <td>
                      <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => onEdit(pack)}>
                        Edit
                      </Button>
                      {pack.source === 'DB' && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={deleting}
                          onClick={() => onResetToDefault(pack)}
                        >
                          Revert
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showEditor} onHide={() => setShowEditor(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit {editId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Enabled</Form.Label>
                <Form.Check
                  type="switch"
                  id="starter-pack-enabled"
                  checked={enabled}
                  onChange={(event) => setEnabled(event.currentTarget.checked)}
                  label={enabled ? 'On' : 'Off'}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Trigger</Form.Label>
                <Form.Control
                  as="select"
                  value={triggerType}
                  onChange={(event) => {
                    const nextTriggerType = event.currentTarget.value as TriggerType;
                    setTriggerType(nextTriggerType);
                    if (nextTriggerType === 'FIXED_LEVEL' && !triggerLevel.trim() && editEffectiveLevel != null) {
                      setTriggerLevel(String(editEffectiveLevel));
                    }
                  }}
                >
                  <option value="FEATURE_UNLOCK">Feature unlock (level configs decide)</option>
                  <option value="FIXED_LEVEL">Fixed level (same for everyone)</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Level</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={triggerType === 'FIXED_LEVEL' ? triggerLevel : editEffectiveLevel ?? ''}
                  disabled={triggerType !== 'FIXED_LEVEL'}
                  onChange={(event) => setTriggerLevel(event.target.value)}
                />
                {triggerType !== 'FIXED_LEVEL' && (
                  <Form.Text className="text-muted">From the live level config</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Offer window (hours)</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={expiryHours}
                  onChange={(event) => setExpiryHours(event.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Value badge (x N)</Form.Label>
                <Form.Control type="number" min={1} value={value} onChange={(event) => setValue(event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Cost (USD)</Form.Label>
                <Form.Control type="number" step="0.01" value={cost} onChange={(event) => setCost(event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Feature (drives the in-game texts/icon)</Form.Label>
                <Form.Control value={feature} onChange={(event) => setFeature(event.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>iOS SKU</Form.Label>
                <Form.Control value={skuIos} onChange={(event) => setSkuIos(event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Android SKU</Form.Label>
                <Form.Control value={skuAndroid} onChange={(event) => setSkuAndroid(event.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          <h6>Rewards</h6>
          {rewards.map((reward, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Row key={index} className="align-items-end mb-2">
              <Col md={3}>
                <Form.Control
                  as="select"
                  value={reward.type}
                  onChange={(event) => setRewardField(index, 'type', event.currentTarget.value)}
                >
                  {REWARD_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                  {!REWARD_TYPES.includes(reward.type) && <option value={reward.type}>{reward.type}</option>}
                </Form.Control>
              </Col>
              <Col md={2}>
                <Form.Control
                  type="number"
                  min={1}
                  value={reward.amount}
                  onChange={(event) => setRewardField(index, 'amount', event.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Control
                  as="select"
                  value={reward.boostType ?? ''}
                  disabled={reward.type !== 'CURRENCY_BOOST'}
                  onChange={(event) => setRewardField(index, 'boostType', event.currentTarget.value)}
                >
                  <option value="">boost…</option>
                  {BOOST_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col md={2}>
                <Form.Control
                  placeholder="category ('any' = player picks)"
                  title="Only for CURRENCY_CATEGORY_FRAGMENT rewards"
                  value={reward.categoryId ?? ''}
                  disabled={reward.type !== 'CURRENCY_CATEGORY_FRAGMENT'}
                  onChange={(event) => setRewardField(index, 'categoryId', event.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Control
                  placeholder="itemId (e.g. sticker_basic_1)"
                  title="Only for STICKER / PROFILE_FRAME / APP_SKIN rewards"
                  value={reward.itemId ?? ''}
                  disabled={!['STICKER', 'PROFILE_FRAME', 'APP_SKIN'].includes(reward.type)}
                  onChange={(event) => setRewardField(index, 'itemId', event.target.value)}
                />
              </Col>
              <Col md={1}>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => setRewards((current) => current.filter((_item, itemIndex) => itemIndex !== index))}
                >
                  ✕
                </Button>
              </Col>
            </Row>
          ))}
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setRewards((current) => [...current, { type: 'CURRENCY_COIN', amount: 1000 }])}
          >
            + Add reward
          </Button>
        </Modal.Body>
        <Modal.Footer>
          {editorError && (
            <div className="text-danger mr-auto" style={{ maxWidth: '65%' }}>
              {editorError}
            </div>
          )}
          <Button variant="secondary" onClick={() => setShowEditor(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={saving} onClick={onSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StarterPacks;
